<template>
  <div>
    <staff-vacation-request-button
      v-if="isStaff && shouldShowActions"
      :staff-id="staffId"
      :year="year"
      :leave-type="leaveType"
      @update-table="reFetchList"
    />

    <div
      v-if="vacationList.length"
      class="table-responsive"
    >
      <span
        v-if="submitting"
        class="spinner-border spinner-border-sm"
        role="status"
        aria-hidden="true"
      />
      <table class="table table-striped table-bordered">
        <thead>
          <tr>
            <th>No</th>
            <th>Start Date</th>
            <th>End Date</th>
            <th>Type</th>
            <th>Reason</th>
            <th class="text-center">Half Day</th>
            <th>Days</th>
            <th>Status</th>
            <th v-if="isStaff && shouldShowActions">
              Amend
            </th>
            <th v-if="isStaff && shouldShowActions">
              Nudge
            </th>
            <th v-if="isManager && shouldShowActions" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(vacation, key) in vacationList"
            :key="vacation.id"
          >
            <td>
              {{ ++key }}
            </td>
            <td>{{ vacation.start_date }}</td>
            <td>{{ vacation.end_date }}</td>
            <td>{{ showType(vacation) }}</td>
            <td>{{ vacation.reason }}</td>
            <td class="text-center">
              <span v-if="vacation.half_day === 1 || vacation.half_day === true" class="badge bg-info">
                Yes
              </span>
              <span v-else>-</span>
            </td>
            <td>
              <span v-if="calculatedDays[vacation.id] !== undefined">
                {{ calculatedDays[vacation.id] }}
              </span>
              <span v-else-if="calculatingDays.includes(vacation.id)" class="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              <span v-else>{{ vacation.total_days }}</span>
            </td>
            <td>
              <span
                class="badge"
                :class="statusBadgeClass(vacation)"
              >
                <i
                  :class="statusIcon(vacation)"
                  class="me-1"
                  aria-hidden="true"
                />
                {{ getStatus(vacation) }}
              </span>
            </td>
            <td v-if="isStaff && shouldShowActions">
              <button
                type="button"
                class="btn btn-success"
                :disabled="isSubmitted(vacation) || vacation.is_rejected || submitting"
                @click="submitRequest(vacation.id)"
              >
                Submit
              </button>
              <button
                type="button"
                class="btn btn-warning"
                :disabled="canCancel(vacation) || vacation.is_rejected || submitting"
                @click="cancelRequest(vacation.id)"
              >
                Cancel Request
              </button>
            </td>
            <td v-if="isStaff && shouldShowActions">
              <button
                type="button"
                class="btn btn-info"
                :disabled="vacation.is_approved || vacation.is_rejected || !vacation.submitted || submitting || reminding"
                @click="submitRequest(vacation.id, 'remind')"
              >
                Remind
              </button>
            </td>
            <td v-if="isManager && shouldShowActions">
              <button
                type="button"
                class="btn btn-success"
                :disabled="vacation.is_approved || submitting "
                @click="approveRequest(vacation)"
              >
                Approve
              </button>
              <button
                type="button"
                class="btn btn-danger"
                :disabled="vacation.is_rejected || submitting"
                @click="rejectRequest(vacation)"
              >
                Reject
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else>
      <p>No Vacation Request Existed</p>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
    name: "VacationListTable",
    props: {
        vacationList: {
            type: Array,
            required: true,
        },
        year: {
            type: Number,
            required: true,
        },
        staffId: {
            type: Number,
            required: true,
        },
        isManager: {
            type: Boolean,
            required: true,
        },
        leaveType: {
            type: String,
            required: false,
            default: "vacation",
        },
        readOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
        isSuperAdmin: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            submitting: false,
            reminding: false,
            calculatedDays: {},
            calculatingDays: [],
        };
    },
    computed: {
        isStaff() {
            return !this.isManager;
        },
        shouldShowActions() {
            return !this.readOnly || this.isSuperAdmin;
        },
    },
    watch: {
        vacationList: {
            immediate: true,
            handler(newList) {
                if (newList && newList.length > 0) {
                    this.calculateDaysForVacations(newList);
                }
            },
        },
    },
    methods: {
        isApproved(vacation) {
            return vacation.is_approved === 1;
        },
        isSubmitted(vacation) {
            return vacation.submitted === 1;
        },
        canCancel(vacation) {
            return vacation.is_approved === 1;
        },
        getStatus(vacation) {
            if (this.isApproved(vacation)) {
                return "Approved";
            } else if (vacation.is_rejected) {
                return "Rejected";
            } else {
                if (vacation.submitted) return "Submitted";
                else return "Draft";
            }
        },
        statusBadgeClass(vacation) {
            if (this.isApproved(vacation)) return "bg-success";
            if (vacation.is_rejected) return "bg-danger";
            return vacation.submitted === 1 ? "bg-info" : "bg-secondary";
        },
        statusIcon(vacation) {
            if (this.isApproved(vacation)) return "fa fa-check";
            if (vacation.is_rejected) return "fa fa-times";
            return vacation.submitted === 1 ? "fa fa-clock" : "fa fa-edit";
        },
        showType(vacation) {
            return vacation.sick_leave === 1 ? "Sick Leave" : "Vacation";
        },
        async calculateDaysForVacations(vacations) {
            for (const vacation of vacations) {
                if (this.calculatedDays[vacation.id] === undefined && !this.calculatingDays.includes(vacation.id)) {
                    await this.calculateDays(vacation);
                }
            }
        },
        async calculateDays(vacation) {
            try {
                this.calculatingDays.push(vacation.id);
                
                const year = this.year;
                
                const response = await window.axios.post('/admin/vacation/api/calculate-days', {
                    vacation_end_year: vacation.end_year,
                    vacation_start_date: vacation.start_date,
                    end_date: vacation.end_date,
                    half_day: vacation.half_day === 1,
                    calculation_type: 'total',
                    year: year,
                });

                if (response.data.type === 'success') {
                    this.$set(this.calculatedDays, vacation.id, response.data.data.days);
                }
            } catch (error) {
                console.error('Error calculating days for vacation:', vacation.id, error);
            } finally {
                const index = this.calculatingDays.indexOf(vacation.id);
                if (index > -1) {
                    this.calculatingDays.splice(index, 1);
                }
            }
        },
        reFetchList() {
            this.$emit("get-list", this.leaveType);
        },
        async approveRequest(vacation) {
            try {
                if (confirm("Do you want to approve this request?") === false) {
                    return;
                }

                this.submitting = true;
                await this.managerUpdateRequest({ id: vacation.id, status: "approved", type: "vacation" });
                alert("Leave Request Approved");
                this.$emit("get-list", this.leaveType);
            } catch (error) {
                alert("Error Occured");
                console.log(error);
            } finally {
                this.submitting = false;
            }
        },
        async rejectRequest(vacation) {
            try {
                if (confirm("Do you want to reject this request?") === false) {
                    return;
                }

                this.submitting = true;
                await this.managerUpdateRequest({ id: vacation.id, status: "rejected", type: "vacation" });
                alert("Vacation Request Rejected");
                this.$emit("get-list", this.leaveType);
            } catch (error) {
                alert("Error Occured");
                console.log(error);
            } finally {
                this.submitting = false;
            }
        },
        async submitRequest(vacationId, type = "submit") {
            try {
                let confirmText = "submit";
                if (type === "remind") {
                    confirmText = "remind";
                }

                if (confirm(`Do you want to ${confirmText} this request?`) === false) {
                    return;
                }

                if (type === "remind") {
                    this.reminding = true;
                } else {
                    this.submitting = true;
                }

                await this.submitVacationRequest({ id: vacationId, type: type });

                let message = "Leave Request Submitted";
                if (type === "remind") {
                    message = "Leave Request Reminded";
                }
                alert(message);
                this.$emit("get-list", this.leaveType);
            } catch (error) {
                alert("Error Occured");
                console.log(error);
            } finally {
                this.submitting = false;
                this.reminding = false;
            }
        },
        async cancelRequest(vacationId) {
            try {
                this.submitting = true;
                await this.cancelVacationRequest({ id: vacationId });
                alert("Vacation Request Cancelled");
                this.$emit("get-list", this.leaveType);
            } catch (error) {
                alert("Error Occured");
                console.log(error);
            } finally {
                this.submitting = false;
            }
        },
        ...mapActions({
            cancelVacationRequest: "vacation/cancelRequest",
            submitVacationRequest: "vacation/submitRequest",
            getVacationList: "vacation/getStaffVacations",
            managerUpdateRequest: "vacation/managerUpdateRequest",
        }),
    },
};
</script>

